import { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  doc,
  updateDoc,
} from "firebase/firestore";

function Users() {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");

  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [selectedUser, setSelectedUser] = useState(null);
  const [banReason, setBanReason] = useState("");

  const reasons = [
    "Abusive behavior",
    "Fake account",
    "Cheating detected",
    "Multiple accounts",
    "Payment fraud",
  ];

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    if (loading || !hasMore) return;

    setLoading(true);

    let q;

    if (lastDoc) {
      q = query(
        collection(db, "users"),
        orderBy("createdAt", "desc"),
        startAfter(lastDoc),
        limit(20)
      );
    } else {
      q = query(
        collection(db, "users"),
        orderBy("createdAt", "desc"),
        limit(20)
      );
    }

    const snap = await getDocs(q);

    if (snap.empty) {
      setHasMore(false);
      setLoading(false);
      return;
    }

    const newUsers = snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));

    setUsers((prev) => {
      const map = new Map();
      [...prev, ...newUsers].forEach((u) => map.set(u.id, u));
      return Array.from(map.values());
    });

    setLastDoc(snap.docs[snap.docs.length - 1]);
    setLoading(false);
  };

  // 🔍 SEARCH
  useEffect(() => {
    const s = search.toLowerCase();

   const filteredData = users.filter((u) => {
  return (
    (u.name || "").toLowerCase().includes(s) ||
    (u.email || "").toLowerCase().includes(s) ||
    (u.uid || "").toLowerCase().includes(s) // 🔥 UID SEARCH
  );
});
    setFiltered(filteredData);
  }, [search, users]);

  // 🚫 BAN
  const confirmBan = async () => {
    if (!selectedUser || !banReason) return;

    await updateDoc(doc(db, "users", selectedUser.id), {
      isBanned: true,
      banReason,
      bannedAt: new Date(),
    });

    setUsers((prev) =>
      prev.map((u) =>
        u.id === selectedUser.id
          ? { ...u, isBanned: true, banReason }
          : u
      )
    );

    setSelectedUser(null);
    setBanReason("");
  };

  // ✅ UNBAN
  const unbanUser = async (u) => {
    await updateDoc(doc(db, "users", u.id), {
      isBanned: false,
      banReason: "",
    });

    setUsers((prev) =>
      prev.map((x) =>
        x.id === u.id ? { ...x, isBanned: false } : x
      )
    );
  };

  return (
    <div style={container}>
      <h1 style={title}>👥 Users</h1>

      {/* SEARCH */}
      <input
        placeholder="🔍 Search by name or email"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={searchBox}
      />

      {/* USERS */}
      <div style={grid}>
        {(search ? filtered : users).map((u) => {
          const balance =
            (u.wager || 0) + (u.withdrawable || 0);

          return (
            <div
              key={u.id}
              style={{
                ...card,
                border: u.isBanned
                  ? "1px solid rgba(255,0,0,0.4)"
                  : "1px solid rgba(255,255,255,0.05)",
                opacity: u.isBanned ? 0.6 : 1,
              }}
            >
              <h3>{u.name || "User"}</h3>
              <p style={email}>{u.email}</p>

              <p style={balanceText}>₹{balance}</p>

              {u.isBanned ? (
                <>
                  <p style={banText}>🚫 {u.banReason}</p>
                  <button
                    style={unbanBtn}
                    onClick={() => unbanUser(u)}
                  >
                    Unban
                  </button>
                </>
              ) : (
                <button
                  style={banBtn}
                  onClick={() => setSelectedUser(u)}
                >
                  Ban User
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* LOAD MORE */}
      {hasMore && (
        <button style={loadBtn} onClick={loadUsers}>
          {loading ? "Loading..." : "Load More"}
        </button>
      )}

      {/* MODAL */}
      {selectedUser && (
        <div style={modal}>
          <div style={modalBox}>
            <h3>Ban User</h3>

            {reasons.map((r) => (
              <label key={r} style={radio}>
                <input
                  type="radio"
                  name="reason"
                  onChange={() => setBanReason(r)}
                />
                {r}
              </label>
            ))}

            <div style={{ marginTop: 15 }}>
              <button style={confirmBtn} onClick={confirmBan}>
                Confirm
              </button>
              <button
                style={cancelBtn}
                onClick={() => setSelectedUser(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* 🔥 STYLES */

const container = {
  padding: 20,
  color: "white",
  background: "linear-gradient(135deg, #0F1221, #0A0D1A)",
  minHeight: "100vh",
};

const title = {
  marginBottom: 20,
};

const searchBox = {
  width: "100%",
  padding: 10,
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.1)",
  background: "#1A1F2E",
  color: "#fff",
  marginBottom: 20,
};

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 20,
};

const card = {
  background: "#1A1F2E",
  padding: 16,
  borderRadius: 12,
};

const email = {
  fontSize: 12,
  opacity: 0.6,
};

const balanceText = {
  marginTop: 8,
  fontWeight: "bold",
};

const banText = {
  color: "#ef4444",
  fontSize: 12,
  marginTop: 6,
};

const banBtn = {
  marginTop: 10,
  background: "#dc2626",
  color: "#fff",
  border: "none",
  padding: "8px",
  borderRadius: 8,
  cursor: "pointer",
};

const unbanBtn = {
  marginTop: 10,
  background: "#16a34a",
  color: "#fff",
  border: "none",
  padding: "8px",
  borderRadius: 8,
  cursor: "pointer",
};

const loadBtn = {
  marginTop: 20,
  padding: 10,
  width: "100%",
  background: "#2563eb",
  border: "none",
  color: "#fff",
  borderRadius: 10,
  cursor: "pointer",
};

const modal = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(0,0,0,0.7)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const modalBox = {
  background: "#1A1F2E",
  padding: 20,
  borderRadius: 12,
  width: 300,
};

const radio = {
  display: "block",
  marginBottom: 8,
  fontSize: 14,
};

const confirmBtn = {
  background: "#dc2626",
  color: "#fff",
  border: "none",
  padding: "8px 12px",
  borderRadius: 8,
  marginRight: 10,
};

const cancelBtn = {
  background: "#374151",
  color: "#fff",
  border: "none",
  padding: "8px 12px",
  borderRadius: 8,
};

export default Users;